import { useState } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import type { GameweekDetail, GameweekResult } from '../types'

export interface SeasonArchiveController {
  /** One scored row per gameweek that has produced points — the season's authoritative totals. */
  seasonHistory: GameweekResult[]
  setSeasonHistory: Dispatch<SetStateAction<GameweekResult[]>>
  /** The full fixtures + picks + results breakdown behind each scored gameweek. */
  seasonDetails: Record<number, GameweekDetail>
  setSeasonDetails: Dispatch<SetStateAction<Record<number, GameweekDetail>>>
}

/**
 * The two season-wide slices.
 *
 * **Read-only client state**: the server scores every gameweek continuously,
 * so these are a local mirror of rows this client does not own. The autosaves that used to write
 * them back are gone — a client that booted before a fixture finished would have replaced the
 * server's newer scores with its stale copy, and points would visibly go backwards.
 *
 * The setters remain, because something still has to put the server's answer into React: the
 * bootstrap load, the phase poll (which re-reads whenever the server reports a gameweek rescored),
 * and the dev tools, which drive server-side endpoints and re-read afterwards.
 */
export function useSeasonArchive(): SeasonArchiveController {
  const [seasonHistory, setSeasonHistory] = useState<GameweekResult[]>([])
  const [seasonDetails, setSeasonDetails] = useState<Record<number, GameweekDetail>>({})

  return { seasonHistory, setSeasonHistory, seasonDetails, setSeasonDetails }
}
